import { useState, useEffect } from 'react';
import { getThreatActors, getClients, assignThreatActor, unassignThreatActor, syncThreatActors } from '../lib/api';

const LEVEL_COLOR = {
  CRITICAL: { bg: 'rgba(239,68,68,0.12)', border: '#EF4444', text: '#EF4444' },
  HIGH:     { bg: 'rgba(245,158,11,0.12)', border: '#F59E0B', text: '#F59E0B' },
  MEDIUM:   { bg: 'rgba(59,130,246,0.12)', border: '#3B82F6', text: '#3B82F6' },
  LOW:      { bg: 'rgba(156,163,175,0.12)', border: '#6B7280', text: '#6B7280' },
};

const s = {
  page: { padding: '24px', overflowY: 'auto', flex: 1 },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 },
  title: { fontSize: 16, fontWeight: 600, color: 'var(--gold)' },
  subtitle: { fontSize: 12, color: 'var(--text2)', marginTop: 2 },
  syncBtn: { fontSize: 12, padding: '7px 16px', background: 'var(--gold)', color: '#0a0a0a', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 600 },
  filters: { display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' },
  filterInput: { fontSize: 12, padding: '7px 12px', background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 7, color: 'var(--text1)', width: 180 },
  levelTab: { fontSize: 11, padding: '5px 12px', borderRadius: 6, cursor: 'pointer', border: '0.5px solid var(--border)', background: 'var(--surface)', color: 'var(--text2)', letterSpacing: '0.04em' },
  actorGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 14 },
  card: { background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '16px' },
  cardHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 },
  actorName: { fontSize: 14, fontWeight: 600, color: 'var(--text1)' },
  aliases: { fontSize: 11, color: 'var(--text2)', marginTop: 2 },
  levelBadge: { fontSize: 10, padding: '3px 10px', borderRadius: 10, fontWeight: 600, letterSpacing: '0.05em', flexShrink: 0 },
  meta: { display: 'flex', gap: 12, marginBottom: 10, flexWrap: 'wrap' },
  metaItem: { fontSize: 11, color: 'var(--text2)', display: 'flex', alignItems: 'center', gap: 4 },
  desc: { fontSize: 12, color: 'var(--text2)', lineHeight: 1.5, marginBottom: 10 },
  tags: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 },
  tag: { fontSize: 10, padding: '2px 8px', background: 'rgba(200,180,138,0.1)', border: '0.5px solid rgba(200,180,138,0.2)', borderRadius: 4, color: 'var(--gold)' },
  assignRow: { display: 'flex', gap: 8, alignItems: 'center', borderTop: '0.5px solid var(--border)', paddingTop: 10, marginTop: 4 },
  assignSelect: { flex: 1, fontSize: 11, padding: '5px 8px', background: 'var(--bg)', border: '0.5px solid var(--border)', borderRadius: 6, color: 'var(--text1)' },
  assignBtn: { fontSize: 11, padding: '5px 12px', background: 'var(--gold)', color: '#0a0a0a', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 },
  mitreLink: { fontSize: 10, color: 'var(--gold)', textDecoration: 'none', opacity: 0.8 },
  empty: { textAlign: 'center', color: 'var(--text2)', fontSize: 13, padding: '40px 0' },
  syncMsg: { fontSize: 12, color: 'var(--text2)', marginLeft: 12 },
};

export default function ThreatActors() {
  const [actors, setActors] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('ALL');
  const [selectedClient, setSelectedClient] = useState({});
  const [assigning, setAssigning] = useState({});

  const load = () => {
    setLoading(true);
    Promise.all([getThreatActors(), getClients()])
      .then(([ta, cl]) => {
        setActors(ta.threat_actors || []);
        setClients(cl.clients || Object.values(cl).filter(c => typeof c === 'object') || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    setSyncMsg('');
    try {
      await syncThreatActors();
      setSyncMsg('Sync started — data will update within a minute');
      setTimeout(() => { load(); setSyncMsg(''); }, 8000);
    } catch (e) {
      setSyncMsg('Sync failed: ' + e.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleAssign = async (actorId) => {
    const clientId = selectedClient[actorId];
    if (!clientId) return;
    setAssigning(a => ({ ...a, [actorId]: true }));
    try {
      await assignThreatActor(actorId, clientId, 'Manually assigned by admin');
      setSyncMsg(`Assigned to ${clientId}`);
      setTimeout(() => setSyncMsg(''), 3000);
    } catch (e) {
      setSyncMsg('Error: ' + e.message);
    } finally {
      setAssigning(a => ({ ...a, [actorId]: false }));
    }
  };

  const LEVELS = ['ALL', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

  const filtered = actors.filter(a => {
    const matchLevel = levelFilter === 'ALL' || a.threat_level === levelFilter;
    const q = search.toLowerCase();
    const matchSearch = !q || a.name.toLowerCase().includes(q) ||
      (a.aliases || []).some(al => al.toLowerCase().includes(q)) ||
      (a.origin_country || '').toLowerCase().includes(q) ||
      (a.target_industries || []).some(i => i.toLowerCase().includes(q)) ||
      (a.target_regions || []).some(r => r.toLowerCase().includes(q));
    return matchLevel && matchSearch;
  });

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <div style={s.title}>Threat Intelligence</div>
          <div style={s.subtitle}>{actors.length} threat actors in database</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {syncMsg && <span style={s.syncMsg}>{syncMsg}</span>}
          <button style={s.syncBtn} onClick={handleSync} disabled={syncing}>
            {syncing ? 'Syncing…' : 'Sync MITRE ATT&CK'}
          </button>
        </div>
      </div>

      <div style={s.filters}>
        <input
          style={s.filterInput}
          placeholder="Search actors, industries, regions…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {LEVELS.map(lv => (
          <button
            key={lv}
            style={{
              ...s.levelTab,
              ...(levelFilter === lv ? {
                background: lv === 'ALL' ? 'var(--gold-dim)' : (LEVEL_COLOR[lv]?.bg || 'var(--gold-dim)'),
                border: `0.5px solid ${lv === 'ALL' ? 'var(--gold)' : (LEVEL_COLOR[lv]?.border || 'var(--gold)')}`,
                color: lv === 'ALL' ? 'var(--gold)' : (LEVEL_COLOR[lv]?.text || 'var(--gold)'),
              } : {})
            }}
            onClick={() => setLevelFilter(lv)}
          >{lv}</button>
        ))}
      </div>

      {loading ? (
        <div style={s.empty}>Loading threat intelligence…</div>
      ) : filtered.length === 0 ? (
        <div style={s.empty}>No threat actors match your filters</div>
      ) : (
        <div style={s.actorGrid}>
          {filtered.map(actor => {
            const lc = LEVEL_COLOR[actor.threat_level] || LEVEL_COLOR.LOW;
            return (
              <div key={actor.id} style={{ ...s.card, borderColor: lc.border + '55' }}>
                <div style={s.cardHeader}>
                  <div>
                    <div style={s.actorName}>{actor.name}</div>
                    {actor.aliases?.length > 0 && (
                      <div style={s.aliases}>aka {actor.aliases.slice(0, 3).join(', ')}</div>
                    )}
                  </div>
                  <span style={{ ...s.levelBadge, background: lc.bg, border: `0.5px solid ${lc.border}`, color: lc.text }}>
                    {actor.threat_level}
                  </span>
                </div>

                <div style={s.meta}>
                  {actor.origin_country && (
                    <span style={s.metaItem}>🌍 {actor.origin_country}</span>
                  )}
                  {actor.last_active && (
                    <span style={s.metaItem}>📅 Last active: {actor.last_active}</span>
                  )}
                  {actor.mitre_id && (
                    <a href={actor.source_url || '#'} target="_blank" rel="noreferrer" style={s.mitreLink}>
                      MITRE {actor.mitre_id} ↗
                    </a>
                  )}
                </div>

                {actor.description && (
                  <div style={s.desc}>{actor.description.slice(0, 180)}{actor.description.length > 180 ? '…' : ''}</div>
                )}

                {actor.target_industries?.length > 0 && (
                  <div style={s.tags}>
                    <span style={{ ...s.metaItem, fontSize: 10 }}>Targets:</span>
                    {actor.target_industries.slice(0, 4).map(i => (
                      <span key={i} style={s.tag}>{i}</span>
                    ))}
                  </div>
                )}

                {actor.attack_methods?.length > 0 && (
                  <div style={s.tags}>
                    {actor.attack_methods.slice(0, 3).map(m => (
                      <span key={m} style={{ ...s.tag, background: 'rgba(239,68,68,0.08)', borderColor: 'rgba(239,68,68,0.2)', color: '#EF4444' }}>{m}</span>
                    ))}
                  </div>
                )}

                <div style={s.assignRow}>
                  <select
                    style={s.assignSelect}
                    value={selectedClient[actor.id] || ''}
                    onChange={e => setSelectedClient(sc => ({ ...sc, [actor.id]: e.target.value }))}
                  >
                    <option value="">Assign to client…</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.name || c.id}</option>
                    ))}
                  </select>
                  <button
                    style={{ ...s.assignBtn, opacity: selectedClient[actor.id] && !assigning[actor.id] ? 1 : 0.5 }}
                    onClick={() => handleAssign(actor.id)}
                    disabled={!selectedClient[actor.id] || assigning[actor.id]}
                  >
                    {assigning[actor.id] ? '…' : 'Assign'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
